// @tennis-matches/services/trainerstunden/sessions
// Service for managing trainerstunden sessions

import { getSupabaseClient } from "../supabase/index.js";
import { BILLING_SESSION_LOCKED_ERROR } from "./billing-session-lock.js";
import {
  isHubReadScopeCurrent,
  registerHubCacheClear,
  resolveHubReadScope,
} from "../hub/hub-scoped-cache.js";
import { attachSessionExternalCourtBookings } from "./session-external-court.js";

const SOFT_DELETE_CACHE_KEY = "tm.trainerstunden_sessions.supports_deleted_at";

/** @type {Promise<{ok: boolean, data?: Array, error?: any}>|null} */
let __loadSessionsInflight = null;
/** @type {Promise<{ok: boolean, data?: Array, error?: any}>|null} */
let __loadDeletedSessionsInflight = null;
/** @type {Map<string, Promise<{ok: boolean, data?: Array, error?: any}>>} */
const __loadHubRelevantSessionsInflight = new Map();
/** @type {Map<string, { ts: number, value: {ok: boolean, data?: Array, error?: any} }>} */
const __loadHubRelevantSessionsCache = new Map();
const HUB_RELEVANT_SESSIONS_CACHE_MS = 15000;

const HUB_PARENT_BOOKING_STATUSES = Object.freeze([
  "pending_trainer",
  "missed_chance",
  "pending_student",
]);
/** @type {boolean|null} */
let __supportsDeletedAtColumn = null;

/** Schlanke Spalten für Hub-Attention / Trainer-Confirm (nur reale DB-Spalten). */
const HUB_RELEVANT_SESSION_SELECT = [
  "id",
  "date",
  "start_time",
  "duration_min",
  "title",
  "location",
  "location_id",
  "trainer_id",
  "planned_ids",
  "present_ids",
  "declined_ids",
  "attendance_confirmed",
  "status",
  "split_base",
  "parent_booking_status",
  "parent_booking_missed_at",
  "deleted_at",
].join(",");

function isSessionRowSoftDeleted(row) {
  const v = row?.deleted_at;
  return v != null && String(v).trim() !== "";
}

function isMissingColumnOrBadRequestError(error) {
  if (!error) return false;
  const status = Number(error?.status ?? error?.statusCode ?? 0);
  if (status === 400) return true;
  const code = String(error?.code || "").trim();
  const msg =
    `${String(error?.message || "")} ${String(error?.details || "")} ${String(error?.hint || "")}`.toLowerCase();
  return (
    code === "42703" ||
    code === "PGRST100" ||
    code === "PGRST204" ||
    msg.includes("deleted_at") ||
    (msg.includes("column") && msg.includes("does not exist")) ||
    msg.includes("failed to parse") ||
    msg.includes("schema cache")
  );
}

function readSoftDeleteCache() {
  try {
    if (typeof localStorage === "undefined") return null;
    const v = localStorage.getItem(SOFT_DELETE_CACHE_KEY);
    if (v === "1") return true;
    if (v === "0") {
      localStorage.removeItem(SOFT_DELETE_CACHE_KEY);
    }
  } catch (_) {}
  return null;
}

function writeSoftDeleteCache(supports) {
  __supportsDeletedAtColumn = supports;
  try {
    if (typeof localStorage !== "undefined") {
      if (supports) {
        localStorage.setItem(SOFT_DELETE_CACHE_KEY, "1");
      } else {
        localStorage.removeItem(SOFT_DELETE_CACHE_KEY);
      }
    }
  } catch (_) {}
}

function invalidateSoftDeleteSupportCache() {
  __supportsDeletedAtColumn = null;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(SOFT_DELETE_CACHE_KEY);
    }
  } catch (_) {}
}

/**
 * Prüft einmalig, ob `deleted_at` in PostgREST/DB verfügbar ist (Migration 2026_06_69).
 * @param {import("@supabase/supabase-js").SupabaseClient} supabase
 */
async function resolveSupportsDeletedAtColumn(supabase) {
  if (__supportsDeletedAtColumn !== null) return __supportsDeletedAtColumn;
  const cached = readSoftDeleteCache();
  if (cached !== null) {
    __supportsDeletedAtColumn = cached;
    return cached;
  }
  const { error } = await supabase
    .from("trainerstunden_sessions")
    .select("deleted_at")
    .limit(1);
  const supports = !(error && isMissingColumnOrBadRequestError(error));
  writeSoftDeleteCache(supports);
  if (!supports && import.meta.env?.DEV) {
    console.warn(
      "[trainerstunden_sessions] Spalte deleted_at fehlt in API/DB — loadDeletedSessions wird übersprungen (Migration 2026_06_69?).",
    );
  }
  return supports;
}

async function resolveSupportsDeletedAtColumnFresh(supabase) {
  invalidateSoftDeleteSupportCache();
  return resolveSupportsDeletedAtColumn(supabase);
}

async function ensureSoftDeleteSupported(supabase) {
  let supports = await resolveSupportsDeletedAtColumn(supabase);
  if (!supports) {
    supports = await resolveSupportsDeletedAtColumnFresh(supabase);
  }
  return supports;
}

function describeSessionsQuery(op, extra = {}) {
  return {
    table: "trainerstunden_sessions",
    op,
    select: extra.select ?? "*",
    filters: extra.filters ?? [],
    order: extra.order ?? [],
  };
}

function logSessionsQueryDev(meta, error) {
  try {
    if (typeof import.meta === "undefined" || !import.meta.env?.DEV) return;
    if (typeof console === "undefined" || !console.debug) return;
    console.debug(
      "[trainerstunden_sessions] query",
      meta,
      error ? { error } : {},
    );
  } catch (_) {}
}

function applySessionListFilters(query, options) {
  let q = query;
  const filters = [];
  if (options.dateFrom) {
    q = q.gte("date", options.dateFrom);
    filters.push(["date", "gte", options.dateFrom]);
  }
  if (options.dateUntil) {
    q = q.lte("date", options.dateUntil);
    filters.push(["date", "lte", options.dateUntil]);
  }
  if (options.confirmed !== undefined) {
    q = q.eq("attendance_confirmed", options.confirmed);
    filters.push(["attendance_confirmed", "eq", options.confirmed]);
  }
  return { query: q, filters };
}

/**
 * Load all sessions for the current user
 * @param {Object} options - Filter options
 * @returns {Promise<{ok: boolean, data?: Array, error?: any}>}
 */
export async function loadSessions(options = {}) {
  const hasFilters = Boolean(
    options?.dateFrom ||
    options?.dateUntil ||
    options?.confirmed !== undefined ||
    options?.includeDeleted === true,
  );
  if (!hasFilters && __loadSessionsInflight) return __loadSessionsInflight;

  const run = (async () => {
    try {
      const supabase = await getSupabaseClient();
      if (!supabase) {
        return { ok: false, error: "no-supabase" };
      }

      const meta = describeSessionsQuery("loadSessions", {
        order: ["date.desc", "start_time.desc"],
      });
      let query = supabase.from("trainerstunden_sessions").select("*");
      const applied = applySessionListFilters(query, options);
      query = applied.query;
      meta.filters = applied.filters;

      query = query
        .order("date", { ascending: false })
        .order("start_time", { ascending: false });

      const { data, error } = await query;

      if (error) {
        logSessionsQueryDev(meta, error);
        console.warn("[loadSessions] Error:", error);
        return { ok: false, error };
      }

      const rows = data || [];
      const includeDeleted = options.includeDeleted === true;
      const filtered = includeDeleted
        ? rows
        : rows.filter((r) => !isSessionRowSoftDeleted(r));
      const out = await attachSessionExternalCourtBookings(filtered);
      return { ok: true, data: out };
    } catch (e) {
      console.error("[loadSessions] Exception:", e);
      return { ok: false, error: e };
    }
  })();

  if (!hasFilters) {
    const promise = run.finally(() => {
      if (__loadSessionsInflight === promise) __loadSessionsInflight = null;
    });
    __loadSessionsInflight = promise;
    return promise;
  }
  return run;
}

function hubRelevantSessionsTodayYmd(now = new Date()) {
  const p = (n) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}`;
}

/**
 * Spiegelt die Hub-Query-Semantik (ohne DB): Parent-Booking-Status offen
 * oder unbestätigt mit date <= heute. Soft-deleted zählen nicht.
 * @param {object|null|undefined} session
 * @param {string} todayYmd
 */
export function isHubRelevantSessionCandidate(session, todayYmd) {
  if (!session || isSessionRowSoftDeleted(session)) return false;
  const pbs = String(session?.parent_booking_status || "").trim();
  if (HUB_PARENT_BOOKING_STATUSES.includes(pbs)) return true;
  const d = String(session?.date || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || !/^\d{4}-\d{2}-\d{2}$/.test(todayYmd)) {
    return false;
  }
  if (d > todayYmd) return false;
  return session?.attendance_confirmed !== true;
}

export function clearHubRelevantSessionsCache() {
  __loadHubRelevantSessionsCache.clear();
  __loadHubRelevantSessionsInflight.clear();
}

registerHubCacheClear(clearHubRelevantSessionsCache);

/**
 * Hub-relevante Sessions in zwei gezielten Queries (kein select=*, kein Voll-Fallback).
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<{ok: boolean, data?: Array, error?: any}>}
 */
export async function loadHubRelevantSessions(options = {}) {
  const force = options?.force === true;
  const scope = await resolveHubReadScope();
  const cacheKey = scope.key;
  const now = Date.now();
  if (!force) {
    const cached = __loadHubRelevantSessionsCache.get(cacheKey);
    if (cached && now - cached.ts < HUB_RELEVANT_SESSIONS_CACHE_MS) {
      return cached.value;
    }
    const inflight = __loadHubRelevantSessionsInflight.get(cacheKey);
    if (inflight) return inflight;
  }

  const started = { ...scope };
  const run = (async () => {
    try {
      const supabase = await getSupabaseClient();
      if (!supabase) return { ok: false, error: "no-supabase" };

      const today = hubRelevantSessionsTodayYmd();
      const meta = describeSessionsQuery("loadHubRelevantSessions", {
        select: HUB_RELEVANT_SESSION_SELECT,
        order: ["date.desc", "start_time.desc"],
        filters: [
          [
            "parent_booking_status",
            "in",
            HUB_PARENT_BOOKING_STATUSES.join(","),
          ],
          ["date", "lte", today],
          ["attendance_confirmed", "or", "is.null|eq.false"],
        ],
      });

      const base = () =>
        supabase
          .from("trainerstunden_sessions")
          .select(HUB_RELEVANT_SESSION_SELECT)
          .order("date", { ascending: false })
          .order("start_time", { ascending: false });

      const [parentRes, unconfirmedRes] = await Promise.all([
        base().in("parent_booking_status", [...HUB_PARENT_BOOKING_STATUSES]),
        base()
          .lte("date", today)
          .or("attendance_confirmed.is.null,attendance_confirmed.eq.false"),
      ]);

      if (parentRes.error || unconfirmedRes.error) {
        const error = parentRes.error || unconfirmedRes.error;
        logSessionsQueryDev(meta, error);
        console.warn("[loadHubRelevantSessions] Error:", error);
        return { ok: false, error };
      }

      const byId = new Map();
      for (const row of [
        ...(parentRes.data || []),
        ...(unconfirmedRes.data || []),
      ]) {
        if (!row?.id || isSessionRowSoftDeleted(row)) continue;
        byId.set(String(row.id), row);
      }
      return { ok: true, data: [...byId.values()] };
    } catch (e) {
      console.error("[loadHubRelevantSessions] Exception:", e);
      return { ok: false, error: e };
    }
  })();

  const promise = run
    .then((value) => {
      if (
        value?.ok &&
        isHubReadScopeCurrent(started) &&
        started.key === cacheKey
      ) {
        __loadHubRelevantSessionsCache.set(cacheKey, {
          ts: Date.now(),
          value,
        });
      }
      return value;
    })
    .finally(() => {
      if (__loadHubRelevantSessionsInflight.get(cacheKey) === promise) {
        __loadHubRelevantSessionsInflight.delete(cacheKey);
      }
    });
  __loadHubRelevantSessionsInflight.set(cacheKey, promise);
  return promise;
}

/**
 * Nur soft-gelöschte Sessions (Papierkorb / Wiederherstellung).
 */
export async function loadDeletedSessions(options = {}) {
  if (__loadDeletedSessionsInflight) return __loadDeletedSessionsInflight;

  __loadDeletedSessionsInflight = (async () => {
    try {
      const supabase = await getSupabaseClient();
      if (!supabase) {
        return { ok: false, error: "no-supabase" };
      }

      const supportsSoftDelete = await resolveSupportsDeletedAtColumn(supabase);
      if (!supportsSoftDelete) {
        return {
          ok: true,
          data: [],
          skipped: true,
          reason: "deleted_at-column-missing",
        };
      }

      const meta = describeSessionsQuery("loadDeletedSessions", {
        filters: [["deleted_at", "not.is", null]],
        order: ["deleted_at.desc", "date.desc"],
      });

      let query = supabase
        .from("trainerstunden_sessions")
        .select("*")
        .not("deleted_at", "is", null);
      const applied = applySessionListFilters(query, options);
      query = applied.query;
      meta.filters = [...meta.filters, ...applied.filters];

      query = query
        .order("deleted_at", { ascending: false })
        .order("date", { ascending: false });

      const { data, error } = await query;

      if (error) {
        if (isMissingColumnOrBadRequestError(error)) {
          writeSoftDeleteCache(false);
          logSessionsQueryDev(meta, error);
          return {
            ok: true,
            data: [],
            skipped: true,
            reason: "deleted_at-query-failed",
          };
        }
        logSessionsQueryDev(meta, error);
        console.warn("[loadDeletedSessions] Error:", error);
        return { ok: false, error };
      }

      return { ok: true, data: data || [] };
    } catch (e) {
      console.error("[loadDeletedSessions] Exception:", e);
      return { ok: false, error: e };
    }
  })();

  try {
    return await __loadDeletedSessionsInflight;
  } finally {
    __loadDeletedSessionsInflight = null;
  }
}

/**
 * Create a new session
 * @param {Object} session - Session data
 * @returns {Promise<{ok: boolean, data?: Object, error?: any}>}
 */
export async function createSession(session) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      return { ok: false, error: "no-supabase" };
    }

    const row = { ...(session && typeof session === "object" ? session : {}) };
    if (!String(row.tenant_id || "").trim()) {
      const trainerId = String(row.trainer_id || "").trim();
      if (trainerId) {
        const { data: trainerRow } = await supabase
          .from("trainerstunden_trainers")
          .select("tenant_id")
          .eq("id", trainerId)
          .maybeSingle();
        const tid = String(trainerRow?.tenant_id || "").trim();
        if (tid) row.tenant_id = tid;
      }
    }
    if (!String(row.tenant_id || "").trim()) {
      return {
        ok: false,
        error: {
          message: "training-sessions-write-denied: missing-tenant",
          code: "42501",
        },
      };
    }

    const { data, error } = await supabase
      .from("trainerstunden_sessions")
      .insert([row])
      .select()
      .single();

    if (error) {
      console.warn("[createSession] Error:", error);
      return { ok: false, error };
    }

    return { ok: true, data };
  } catch (e) {
    console.error("[createSession] Exception:", e);
    return { ok: false, error: e };
  }
}

/**
 * Update a session
 * @param {string} id - Session ID
 * @param {Object} updates - Fields to update
 * @returns {Promise<{ok: boolean, data?: Object, error?: any}>}
 */
export async function updateSession(id, updates) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      return { ok: false, error: "no-supabase" };
    }

    const { data, error } = await supabase
      .from("trainerstunden_sessions")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.warn("[updateSession] Error:", error);
      return { ok: false, error };
    }

    return { ok: true, data };
  } catch (e) {
    console.error("[updateSession] Exception:", e);
    return { ok: false, error: e };
  }
}

function isPostgrestNoRowsError(error) {
  if (!error) return false;
  const code = String(error?.code || "").trim();
  return code === "PGRST116";
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
/**
 * @param {unknown} error
 * @returns {string} i18n key
 */
export function resolveSessionDeleteErrorKey(error) {
  if (!error) return "trainerstunden.planning.deleteFailed";
  if (error === BILLING_SESSION_LOCKED_ERROR)
    return "trainerstunden.billingCorrection.blockedSave";
  if (
    error === "session-not-deletable" ||
    error === "not-future-session" ||
    error === "session-not-found"
  ) {
    return "trainerstunden.planning.listDeleteNotFuture";
  }
  if (error === "no-supabase")
    return "trainerstunden.planning.deleteFailedNoSupabase";

  if (typeof error === "object" && error !== null) {
    const code = String(error.code || "")
      .trim()
      .toLowerCase();
    const msg =
      `${String(error.message || "")} ${String(error.details || "")} ${String(error.hint || "")}`.toLowerCase();
    if (
      code === "deleted_at-column-missing" ||
      (msg.includes("deleted_at") && msg.includes("does not exist"))
    ) {
      return "trainerstunden.planning.deleteFailedSoftDeleteMigration";
    }
    if (
      code === "42501" ||
      msg.includes("row-level security") ||
      msg.includes("permission denied")
    ) {
      return "trainerstunden.planning.deleteFailedPermission";
    }
  }

  return "trainerstunden.planning.deleteFailed";
}

export function isDeleteSessionAlreadyGoneError(error) {
  if (!error) return false;
  if (error === "session-not-found" || error === "already-deleted") return true;
  if (typeof error === "object" && error !== null) {
    const code = String(error?.code || "").trim();
    if (code === "already-deleted") return true;
    if (isPostgrestNoRowsError(error)) return true;
  }
  return false;
}

/**
 * Soft-Delete einer Session (Zeile bleibt in der DB).
 * @param {string} id - Session ID
 * @param {{ note?: string }} [options]
 * @returns {Promise<{ok: boolean, data?: Object, error?: any, alreadyDeleted?: boolean}>}
 */
export async function deleteSession(id, options = {}) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      return { ok: false, error: "no-supabase" };
    }

    const supportsSoftDelete = await ensureSoftDeleteSupported(supabase);
    if (!supportsSoftDelete) {
      return {
        ok: false,
        error: {
          code: "deleted_at-column-missing",
          message: "soft-delete-unavailable",
        },
      };
    }

    const sid = String(id || "").trim();
    if (!sid) return { ok: false, error: "missing-id" };

    const { data: existing, error: existingError } = await supabase
      .from("trainerstunden_sessions")
      .select("*")
      .eq("id", sid)
      .maybeSingle();

    if (!existingError && existing && isSessionRowSoftDeleted(existing)) {
      return { ok: true, data: existing, alreadyDeleted: true };
    }

    const note = String(options.note || "").trim() || null;
    let deleted_by = null;
    try {
      const { data: authData } = await supabase.auth.getUser();
      deleted_by = authData?.user?.id ?? null;
    } catch {
      deleted_by = null;
    }

    const { data, error } = await supabase
      .from("trainerstunden_sessions")
      .update({
        deleted_at: new Date().toISOString(),
        deleted_note: note,
        deleted_by,
      })
      .eq("id", sid)
      .select()
      .maybeSingle();

    if (error && !isPostgrestNoRowsError(error)) {
      console.warn("[deleteSession] Error:", error);
      return { ok: false, error };
    }

    if (data) return { ok: true, data };

    const { data: deletedRow, error: deletedLookupError } = await supabase
      .from("trainerstunden_sessions")
      .select("*")
      .eq("id", sid)
      .not("deleted_at", "is", null)
      .maybeSingle();

    if (!deletedLookupError && deletedRow) {
      return { ok: true, data: deletedRow, alreadyDeleted: true };
    }

    if (existing && !isSessionRowSoftDeleted(existing)) {
      console.warn("[deleteSession] Update returned no row for session", sid);
    }

    return { ok: true, data: { id: sid }, alreadyDeleted: true };
  } catch (e) {
    console.error("[deleteSession] Exception:", e);
    return { ok: false, error: e };
  }
}

/**
 * Soft-Delete rückgängig machen.
 * @param {string} id - Session ID
 * @returns {Promise<{ok: boolean, data?: Object, error?: any}>}
 */
export async function restoreSession(id) {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) {
      return { ok: false, error: "no-supabase" };
    }

    const supportsSoftDelete = await ensureSoftDeleteSupported(supabase);
    if (!supportsSoftDelete) {
      return {
        ok: false,
        error: {
          code: "deleted_at-column-missing",
          message: "soft-delete-unavailable",
        },
      };
    }

    const { data, error } = await supabase
      .from("trainerstunden_sessions")
      .update({
        deleted_at: null,
        deleted_note: null,
        deleted_by: null,
      })
      .eq("id", id)
      .select()
      .single();

    if (error) {
      console.warn("[restoreSession] Error:", error);
      return { ok: false, error };
    }

    return { ok: true, data };
  } catch (e) {
    console.error("[restoreSession] Exception:", e);
    return { ok: false, error: e };
  }
}
